// Unit-specific, widget-scoped whole-day restrictions. The bands RPC remains
// authoritative about bookable starts; these rules explain an empty answer.
// Service-wide closures are already supplied by get_service_booking_window.
import { isYmd, weekdayOfYmd } from "./availability-logic";

export type UnitBlackout = {
  unitId: string;
} & (
  | { kind: "blackout_dow"; daysOfWeek: number[] }
  | { kind: "blackout_date"; date: string }
  | { kind: "blackout_range"; startsOn: string; endsOn: string }
);

/** A malformed read is unknown, never an empty schedule (= potentially Full). */
export function readUnitBlackouts(rows: unknown, serviceId: string): UnitBlackout[] | null {
  if (!Array.isArray(rows)) return null;
  const rules: UnitBlackout[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") return null;
    const r = row as Record<string, unknown>;
    if (typeof r.service_id !== "string" || typeof r.is_enabled !== "boolean" ||
        typeof r.applies_widget !== "boolean" ||
        (r.service_unit_id !== null && typeof r.service_unit_id !== "string")) return null;
    if (r.service_id !== serviceId || r.is_enabled !== true || r.applies_widget !== true || r.service_unit_id == null) continue;
    if (typeof r.service_unit_id !== "string" || !r.service_unit_id) return null;
    const unitId = r.service_unit_id;
    if (r.kind === "blackout_dow") {
      if (!Array.isArray(r.days_of_week) || !r.days_of_week.length ||
          !r.days_of_week.every((d) => Number.isInteger(d) && d >= 0 && d <= 6)) return null;
      rules.push({ unitId, kind: r.kind, daysOfWeek: r.days_of_week });
    } else if (r.kind === "blackout_date") {
      if (!isYmd(r.on_date)) return null;
      rules.push({ unitId, kind: r.kind, date: r.on_date });
    } else if (r.kind === "blackout_range") {
      if (!isYmd(r.starts_on) || !isYmd(r.ends_on) || r.ends_on < r.starts_on) return null;
      rules.push({ unitId, kind: r.kind, startsOn: r.starts_on, endsOn: r.ends_on });
    } else {
      return null;
    }
  }
  return rules;
}

/**
 * Selected options not offered on this tenant-local day. Like the engine,
 * blackout_dow is unconditional on those weekdays and date/range blackouts
 * use their own inclusive bounds. constraint_starts_on/ends_on belong only
 * to stay rules; they do not narrow a blackout. No inference from other days'
 * empty bands or another option's restrictions.
 */
export function unitsNotOfferedOn(
  day: string,
  unitIds: readonly string[],
  rules: readonly UnitBlackout[],
): string[] {
  const weekday = weekdayOfYmd(day);
  return unitIds.filter((unitId) => rules.some((r) => {
    if (r.unitId !== unitId) return false;
    if (r.kind === "blackout_dow") return r.daysOfWeek.includes(weekday);
    if (r.kind === "blackout_date") return day === r.date;
    return r.startsOn <= day && day <= r.endsOn;
  }));
}
